/**
 * Google Tag Manager utility functions
 * This file provides helper functions for tracking events in GTM
 */

import { GTM_CONFIG } from '../config/gtm';

// Check if GTM is available
export const isGTMAvailable = () => {
  return typeof window !== 'undefined' && window.dataLayer && GTM_CONFIG.ENABLED;
};

// Push event to dataLayer
export const pushToDataLayer = (eventData) => {
  if (isGTMAvailable()) {
    window.dataLayer.push(eventData);
    if (GTM_CONFIG.DEBUG) {
      console.log('GTM Event pushed:', eventData);
      console.log('Current dataLayer length:', window.dataLayer.length);
    }
  } else {
    if (GTM_CONFIG.DEBUG) {
      console.warn('GTM not available - check if GTM is enabled and dataLayer exists');
      console.log('GTM Enabled:', GTM_CONFIG.ENABLED);
      console.log('Window object:', typeof window);
      console.log('dataLayer exists:', !!window.dataLayer);
    }
  }
};

// Track page view
export const trackPageView = (pageName, pagePath) => {
  pushToDataLayer({
    event: GTM_CONFIG.EVENTS.PAGE_VIEW,
    page_name: pageName,
    page_path: pagePath,
    page_location: window.location.href,
    page_title: document.title
  });
};

// Track quiz events
export const trackQuizEvent = (eventName, eventData = {}) => {
  pushToDataLayer({
    event: GTM_CONFIG.EVENTS.QUIZ_INTERACTION,
    quiz_event: eventName,
    ...eventData
  });
};

// Track form events
export const trackFormEvent = (eventName, formData = {}) => {
  pushToDataLayer({
    event: GTM_CONFIG.EVENTS.FORM_INTERACTION,
    form_event: eventName,
    ...formData
  });
};

// Track user engagement
export const trackEngagement = (action, details = {}) => {
  pushToDataLayer({
    event: GTM_CONFIG.EVENTS.USER_ENGAGEMENT,
    engagement_action: action,
    ...details
  });
};

// Track conversion events
export const trackConversion = (conversionType, value = null, currency = 'USD') => {
  const eventData = {
    event: GTM_CONFIG.EVENTS.CONVERSION,
    conversion_type: conversionType
  };

  if (value !== null) {
    eventData.value = value;
    eventData.currency = currency;
  }

  pushToDataLayer(eventData);
};

// Track errors
export const trackError = (errorType, errorMessage, errorDetails = {}) => {
  pushToDataLayer({
    event: GTM_CONFIG.EVENTS.ERROR,
    error_type: errorType,
    error_message: errorMessage,
    ...errorDetails
  });
};

// Track custom events
export const trackCustomEvent = (eventName, eventData = {}) => {
  pushToDataLayer({
    event: eventName,
    ...eventData
  });
};

// Initialize GTM for DoshaQuiz page
export const initializeDoshaQuizGTM = () => {
  if (isGTMAvailable()) {
    // Track page load
    trackPageView('Dosha Quiz', '/dosha-quiz');
    
    // Track quiz initialization
    trackQuizEvent('quiz_started', {
      quiz_type: 'dosha_quiz',
      timestamp: new Date().toISOString()
    });
  }
};

export default {
  pushToDataLayer,
  trackPageView,
  trackQuizEvent,
  trackFormEvent,
  trackEngagement,
  trackConversion,
  trackError,
  trackCustomEvent,
  initializeDoshaQuizGTM,
  isGTMAvailable
};
